import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getToken, getRefreshToken, getCustomerId, getKycStatus } from '@/utils/common';
import { UserProfile } from '@/types/user';

type kycStatusType = 'NotKyc' | 'UnderReview' | 'Approved' | 'Rejected';

export type AuthState = {
  user: UserProfile | null;
  token?: string | null;
  refreshToken?: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  customerId?: string;
  kycStatus?: kycStatusType | null;
};

export const authInitialState: AuthState = {
  user: null,
  token: getToken(),
  refreshToken: getRefreshToken(),
  isAuthenticated: !!getToken(),
  isLoading: false,
  customerId: getCustomerId(),
  kycStatus: (getKycStatus() as kycStatusType) || null,
};

const slice = createSlice({
  name: 'auth',
  initialState: authInitialState,
  reducers: {
    clearAuthDataAction(state) {
      state.user = null;
      state.token = null;
      state.refreshToken = null;
      state.isAuthenticated = false;
      state.customerId = undefined;
      state.kycStatus = null;
    },

    setDataAndTokenAction(
      state,
      {
        payload,
      }: PayloadAction<{
        user?: UserProfile | null;
        token?: string | null;
        refreshToken?: string | null;
        isAuthenticated?: boolean;
        kycStatus?: kycStatusType | null;
      }>,
    ) {
      if (payload.user !== undefined) {
        state.user = payload.user;
      }
      if (payload.token !== undefined) {
        state.token = payload.token;
      }
      if (payload.refreshToken !== undefined) {
        state.refreshToken = payload.refreshToken;
      }
      if (payload.isAuthenticated !== undefined) {
        state.isAuthenticated = payload.isAuthenticated;
      }
      if (payload.kycStatus !== undefined) {
        state.kycStatus = payload.kycStatus;
      }
    },

    setLoadingAction(state, { payload }: PayloadAction<boolean>) {
      state.isLoading = payload;
    },

    setUserAction(state, { payload }: PayloadAction<UserProfile | null>) {
      state.user = payload;
      state.isAuthenticated = !!payload;
    },

    updateTokensAction(
      state,
      { payload }: PayloadAction<{ token?: string | null; refreshToken?: string | null }>,
    ) {
      if (payload.token !== undefined) {
        state.token = payload.token;
      }
      if (payload.refreshToken !== undefined) {
        state.refreshToken = payload.refreshToken;
      }
      state.isAuthenticated = !!payload.token;
    },

    setCustomerIdAction(state, { payload }: PayloadAction<string | undefined>) {
      state.customerId = payload;
    },
  },
});

export const {
  clearAuthDataAction,
  setDataAndTokenAction,
  setLoadingAction,
  setUserAction,
  updateTokensAction,
  setCustomerIdAction,
} = slice.actions;
export default slice.reducer;
